import type { FormInstance } from "antd";
import { AuthError } from "../../../apis/auth.errors";

// Backend serializer field -> form field name. Kept small on purpose: name the
// form fields after the serializer's wherever possible and this stays empty.
const FIELD_ALIASES: Record<string, string> = {
  contact: "phone",
  new_password: "password",
};

/**
 * Push server-side validation messages onto the matching form fields.
 *
 * Ant Design renders one error line per string, so a Django validator that
 * rejects a password for several reasons at once shows all of them.
 *
 * Returns the messages that matched no rendered field. That return value is the
 * point: `form.setFields` against a name no `Form.Item` renders stores the error
 * against nothing and logs no warning, so without this a backend field rename
 * would turn into silently swallowed errors. Callers toast the leftovers
 * instead.
 */
export function applyFieldErrors(
  form: FormInstance,
  err: AuthError,
  knownFields: string[]
): string[] {
  const fields: { name: string; errors: string[] }[] = [];
  const orphans: string[] = [];

  Object.entries(err.fieldErrors ?? {}).forEach(([key, messages]) => {
    const name = FIELD_ALIASES[key] ?? key;
    if (knownFields.includes(name)) fields.push({ name, errors: messages });
    else orphans.push(...messages);
  });

  if (fields.length) {
    // These persist until the user edits the field, then the normal validation
    // pass clears them — which is exactly the behaviour we want.
    form.setFields(fields);
  }
  return orphans;
}
